// Telegram Bot Service
import { supabase } from '../supabaseClient';

const TELEGRAM_BOT_TOKEN = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;

interface TelegramMessage {
  chat_id: string;
  text: string;
  parse_mode?: 'HTML' | 'Markdown';
}

interface TelegramResponse {
  ok: boolean;
  result?: any;
  error_code?: number;
  description?: string;
}

export class TelegramService {
  private static baseUrl = `https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}`;

  /**
   * Send a message to a Telegram user
   */
  static async sendMessage(chatId: string, text: string): Promise<boolean> {
    if (!TELEGRAM_BOT_TOKEN) {
      console.error('Telegram bot token not configured');
      return false;
    }

    try {
      // Attempt to send directly. Some accounts block getChat without prior permissions,
      // but sendMessage can still succeed if the user started the bot.
      const message: TelegramMessage = {
        chat_id: chatId,
        text: text,
        parse_mode: 'HTML' // Allow basic HTML formatting
      };

      const response = await fetch(`${this.baseUrl}/sendMessage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(message),
      });

      const result: TelegramResponse = await response.json();

      if (result.ok) {
        console.log('Telegram message sent successfully');
        return true;
      } else {
        console.error('Telegram API error:', result.error_code, result.description);
        // If we get 403/400 related to chat initiation, provide actionable hint
        if (result.error_code === 403 || (result.description || '').toLowerCase().includes('bot was blocked') || (result.description || '').toLowerCase().includes('chat not found')) {
          console.warn('Client likely has not started the bot or blocked it.');
        }
        return false;
      }
    } catch (error) {
      console.error('Error sending Telegram message:', error);
      return false;
    }
  }

  /**
   * Get bot information
   */
  static async getBotInfo(): Promise<any> {
    if (!TELEGRAM_BOT_TOKEN) {
      return null;
    }

    try {
      const response = await fetch(`${this.baseUrl}/getMe`);
      const result = await response.json();
      return result.ok ? result.result : null;
    } catch (error) {
      console.error('Error getting bot info:', error);
      return null;
    }
  }

  /**
   * Check if a user has started the bot
   */
  static async checkUserStatus(chatId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/getChat?chat_id=${chatId}`);
      const result = await response.json();
      return result.ok;
    } catch (error) {
      console.error('Error checking user status:', error);
      return false;
    }
  }

  /**
   * Send notification to trainer about client response
   */
  static async notifyTrainer(trainerId: string, clientName: string, action: string): Promise<boolean> {
    try {
      // For now, use the trainer's Telegram user ID directly
      // In the future, this could be stored in the trainers table
      const trainerTelegramId = '8366786049'; // Your Telegram user ID
      
      const message = `📱 <b>Client Update</b>\n\n${clientName} has ${action}.\n\nYou can check their progress in the dashboard.`;
      
      return await this.sendMessage(trainerTelegramId, message);
    } catch (error) {
      console.error('Error notifying trainer:', error);
      return false;
    }
  }

  /**
   * Process incoming message from client
   */
  static async processClientMessage(chatId: string, message: string): Promise<void> {
    try {
      // Check if this is a "done" response to meal reminder
      if (message.toLowerCase().includes('done')) {
        // Get client info
        const { data: clientData, error } = await supabase
          .from('trainers_clients')
          .select('trainer_id, client_name')
          .eq('telegram_user_id', chatId)
          .single();

        if (error || !clientData) {
          console.error('Client not found:', error);
          return;
        }

        // Send confirmation to client
        await this.sendMessage(chatId, `✅ Great job! Your trainer will be notified that you've logged your meals.`);

        // Notify trainer
        await this.notifyTrainer(clientData.trainer_id, clientData.client_name, 'confirmed they logged their meals');

        // Log the interaction
        await supabase
          .from('messages')
          .insert({
            trainer_id: clientData.trainer_id,
            telegram_user_id: chatId,
            client_name: clientData.client_name,
            message: `Client confirmed: ${message}`,
            message_type: 'confirmation',
            is_sent: true
          });
      }
    } catch (error) {
      console.error('Error processing client message:', error);
    }
  }
} 